import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons'
import { Table, TablePaginationConfig, Typography } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { FilterValue, SorterResult } from 'antd/lib/table/interface'
import { t } from 'i18next'
import moment from 'moment'
import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react'
import Moment from 'react-moment'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import {
  Badge,
  ConfirmDeleteModal,
  DeleteIconButton,
  ListPageHeader,
  ListPageLayout,
  PageContentDropdown,
  SectionLoadingState,
  TableBoldColumnContainer,
} from '../../components'
import {
  BadgeColors,
  DEFAULT_PAGE_SIZE,
  NO_DATA_PLACEHOLDER,
  PULL_INTERVAL,
  backEndSortOrders,
  dateFormat,
  ofrDocumentStatus,
  ofrStatusColorKeys,
  ofrStatusTextMap,
  privacyTypes,
  sorterTypeDictionary,
} from '../../constants/constants'
import { useOfrDocumentTemplate } from '../../hooks'
import useMediaQuery from '../../hooks/useMediaQuery'
import { OfrDocumentTemplate } from '../../store/reducers/ofrDocumentTemplateReducer'
import { routePaths } from '../RootPage'
import CreateOfrFormModal from './modals/CreateOfrDocumentTemplateModal'

const SectionLoadingStateContainer = styled.div`
  display: flex;
  height: calc(100vh - 100px);
  margin-top: -64px;
`

interface OfrDocumentTemplateExtended extends OfrDocumentTemplate {
  key: string
}

const Text = Typography.Text

const OfrDocumentTemplateListPage = () => {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [sortOrder, setSortOrder] = useState(backEndSortOrders.ASC)
  const [sortField, setSortField] = useState<string>('title')
  const [total, setTotal] = useState(0)
  const [deleteId, setDeleteId] = useState<string | undefined>(undefined)
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false)
  const isMobile = useMediaQuery('xs')

  const {
    ofrDocumentTemplateList,
    loading,
    deleteOfrDocumentTemplate,
    exportOfrDocumentTemplateList,
    ofrDocumentTemplateListTotalResults,
    refreshOfrDocumentTemplateList,
    isDeletable,
    isCreatable,
  } = useOfrDocumentTemplate({
    pageSize,
    page,
    sortOrder,
    sortField,
  })

  useEffect(() => {
    const interval = setInterval(() => {
      if (
        ofrDocumentTemplateList?.some(
          (ofrForm) =>
            ofrForm.status === ofrDocumentStatus.PROCESSING ||
            ofrForm.status === ofrDocumentStatus.INGESTION
        )
      ) {
        refreshOfrDocumentTemplateList()
      }
    }, PULL_INTERVAL)
    return () => clearInterval(interval)
  }, [refreshOfrDocumentTemplateList, ofrDocumentTemplateList])

  const formattedData: OfrDocumentTemplateExtended[] = useMemo(
    () =>
      ofrDocumentTemplateList?.map((ofrForm) => ({
        ...ofrForm,
        key: ofrForm.id,
        templateType: ofrForm.templateType,
      })) || [],
    [ofrDocumentTemplateList]
  )

  useEffect(() => {
    if (ofrDocumentTemplateList) {
      setTotal(ofrDocumentTemplateList.length)
    }
  }, [ofrDocumentTemplateList])

  const handleCancelDelete = (event: MouseEvent) => {
    event.stopPropagation()
    setDeleteId(undefined)
    setConfirmDeleteModalOpen(false)
  }

  const handleConfirmDelete = (event: MouseEvent) => {
    event.stopPropagation()
    if (deleteId) {
      deleteOfrDocumentTemplate({
        documentId: deleteId,
        navigate: null,
      })
    }
    setDeleteId(undefined)
    setConfirmDeleteModalOpen(false)
  }

  const handleClick = (id: string) => {
    navigate(`${routePaths.OFR_DOCUMENT_TEMPLATE_DETAILS}/${id}`)
  }

  const handleDeleteClick = useCallback((event: MouseEvent, id: string) => {
    event.stopPropagation()
    setDeleteId(id)
    setConfirmDeleteModalOpen(true)
  }, [])

  const handleTableChange = useCallback(
    (
      pagination: TablePaginationConfig,
      _filters: Record<string, FilterValue | null>,
      sorter:
        | SorterResult<OfrDocumentTemplateExtended>
        | SorterResult<OfrDocumentTemplateExtended>[]
    ) => {
      setPage(pagination.current || 1)
      if (pagination.pageSize && pagination.pageSize !== pageSize) {
        setPageSize(pagination.pageSize)
      }

      const isSortArray = Array.isArray(sorter)
      const firstSort = isSortArray ? sorter[0] : sorter

      setSortOrder(sorterTypeDictionary[firstSort.order || 'ascend'])
      setSortField(firstSort.field as string)
    },
    [pageSize]
  )

  const statusIcons = {
    [ofrDocumentStatus.PROCESSING]: <SyncOutlined spin />,
    [ofrDocumentStatus.READY]: <CheckCircleOutlined />,
    [ofrDocumentStatus.FAILED]: <CloseCircleOutlined />,
    [ofrDocumentStatus.INGESTION]: <SyncOutlined spin />,
  }

  const renderStatusTag = (text: string) => {
    const tagText =
      text in ofrStatusTextMap ? ofrStatusTextMap[text] : NO_DATA_PLACEHOLDER
    return (
      <Badge
        icon={statusIcons[text]}
        color={ofrStatusColorKeys[text] || 'default'}
      >
        {tagText || NO_DATA_PLACEHOLDER}
      </Badge>
    )
  }

  const columns: ColumnsType<OfrDocumentTemplateExtended> = [
    {
      title: t('Title'),
      dataIndex: 'title',
      key: 'title',
      width: '60%',
      sorter: (
        a: OfrDocumentTemplateExtended,
        b: OfrDocumentTemplateExtended
      ) => (a.title && b.title ? a.title.localeCompare(b.title) : -1),
      render: (text) => (
        <TableBoldColumnContainer>
          {text || NO_DATA_PLACEHOLDER}
        </TableBoldColumnContainer>
      ),
    },
    {
      title: t('Source'),
      dataIndex: 'dataSourceTitle',
      key: 'dataSourceTitle',
      width: '10%',
      render: (text) => (
        <Badge color={BadgeColors.DATA_SOURCE}>
          {text || NO_DATA_PLACEHOLDER}
        </Badge>
      ),
    },
    {
      title: t('Type'),
      dataIndex: 'templateType',
      key: 'templateType',
      render: (text) => {
        return (
          <Text style={{ width: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {text
              ? t(`ofrDocumentTemplateListPage.ofrDocumentTemplateType.${text}`)
              : NO_DATA_PLACEHOLDER}
          </Text>
        )
      },
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      render: renderStatusTag,
    },

    {
      title: t('Last Update'),
      key: 'lastModifiedDate',
      dataIndex: 'lastModifiedDate',
      sorter: (
        a: OfrDocumentTemplateExtended,
        b: OfrDocumentTemplateExtended
      ) =>
        a.lastModifiedDate && b.lastModifiedDate
          ? moment(a.lastModifiedDate).valueOf() -
            moment(b.lastModifiedDate).valueOf()
          : -1,
      render: (textDate) => (
        <>
          {textDate ? (
            <Moment local format={dateFormat.PRIMARY}>
              {textDate}
            </Moment>
          ) : (
            NO_DATA_PLACEHOLDER
          )}
        </>
      ),
    },

    {
      title: '',
      key: 'action',
      align: 'right',
      render: (text, { id, status, visibility }) => (
        <>
          {visibility !== privacyTypes.PUBLIC && isDeletable && (
            <DeleteIconButton
              disabled={status === ofrDocumentStatus.PROCESSING}
              tooltipTitle={t(
                'ofrDocumentTemplateListPage.deleteAnnotationTemplateTooltip'
              )}
              onClick={(event: MouseEvent) => handleDeleteClick(event, id)}
            />
          )}
        </>
      ),
    },
  ]

  const handleExportOfrList = useCallback(() => {
    exportOfrDocumentTemplateList()
  }, [exportOfrDocumentTemplateList])

  return (
    <>
      <ListPageLayout>
        <ListPageHeader
          title={t('ofrDocumentTemplateListPage.title', {
            count: total,
          })}
          actions={
            <>
              <CreateOfrFormModal disabled={!isCreatable} />
              <PageContentDropdown
                onExport={handleExportOfrList}
                disabled={formattedData.length === 0}
              />
            </>
          }
        />
        {loading ? (
          <SectionLoadingStateContainer>
            <SectionLoadingState />
          </SectionLoadingStateContainer>
        ) : (
          <Table
            rowClassName={(record) => {
              if (record.status === ofrDocumentStatus.PROCESSING) {
                return 'page-list-table-row-disabled'
              }
              return 'page-list-table-row clickable'
            }}
            columns={columns}
            dataSource={formattedData}
            onChange={handleTableChange}
            pagination={{
              current: page,
              total: ofrDocumentTemplateListTotalResults,
              pageSize: pageSize,
              position: ['bottomLeft'],
              showSizeChanger: false,
            }}
            scroll={{
              x: isMobile ? 'max-content' : '100%',
              y: 'calc(100vh - 220px)',
            }}
            onRow={(record, rowIndex) => {
              return {
                onClick: (event) => {
                  // only allow click on row if status is Ready
                  if (record.status === ofrDocumentStatus.READY)
                    handleClick(record.id)
                }, // click row
              }
            }}
          />
        )}
        <ConfirmDeleteModal
          open={confirmDeleteModalOpen}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          confirmButtonLabel={
            t('ofrDocumentTemplateListPage.confirmDeleteModal.yesButton') || ''
          }
        >
          {t('ofrDocumentTemplateListPage.confirmDeleteModal.content')}
        </ConfirmDeleteModal>
      </ListPageLayout>
    </>
  )
}

export default OfrDocumentTemplateListPage
